import {
  type ButtonHTMLAttributes,
  type FC,
  forwardRef,
  type PropsWithChildren,
} from 'react';

import { useGetCommonStore } from '@/store';
import { IconComponent } from '../IconComponent';
import {
  ScButtonClose,
  type ScButtonCloseProps,
  ScButtonFullScreen,
  type ScButtonFullScreenProps,
  ScButtonHeart,
  type ScButtonHeartProps,
  ScButtonMore,
  type ScButtonMoreProps,
  ScButtonTuple,
  type ScButtonTupleProps,
  ScButtonWithArray,
  type ScButtonWithArrayProps,
  ScScrollTopButton,
  type ScScrollTopButtonProps,
  ScTabBarButton,
  type ScTabBarButtonProps,
} from './styled';

export const ButtonClose: FC<
  ScButtonCloseProps & ButtonHTMLAttributes<HTMLButtonElement>
> = (props) => (
  <ScButtonClose {...props}>
    <IconComponent
      className="button-icon"
      name={props.inCircle ? 'crossInCircleIcon' : 'closeIcon'}
      size="40px"
    />
  </ScButtonClose>
);

export const ButtonMore: FC<
  ScButtonMoreProps & ButtonHTMLAttributes<HTMLButtonElement>
> = (props) => (
  <ScButtonMore {...props}>
    <div />
    <div />
    <div />
  </ScButtonMore>
);

export const ButtonHeart: FC<
  ScButtonHeartProps & ButtonHTMLAttributes<HTMLButtonElement>
> = (props) => <ScButtonHeart {...props} />;

export const ButtonFullScreen: FC<
  ScButtonFullScreenProps & ButtonHTMLAttributes<HTMLButtonElement>
> = (props) => <ScButtonFullScreen {...props} />;

export const ButtonWithArray = forwardRef<
  HTMLButtonElement,
  ScButtonWithArrayProps &
    PropsWithChildren &
    ButtonHTMLAttributes<HTMLButtonElement>
>(({ children, ...props }, ref) => {
  const { icon } = props;

  return (
    <ScButtonWithArray ref={ref} {...props}>
      <div className="value">{children}</div>
      <div className="arrow">
        <IconComponent
          className="arrow-icon"
          name="whiteArrowRight"
          size="22px"
        />
      </div>
      {icon && <div className="icon">{icon}</div>}
    </ScButtonWithArray>
  );
});

export const ButtonSupport = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement>
>((props, ref) => (
  <ScTabBarButton ref={ref} {...props} aria-label="support">
    <IconComponent className="button-icon" name="supportIcon" size="40px" />
  </ScTabBarButton>
));

export const ButtonSearch = forwardRef<
  HTMLButtonElement,
  ScButtonTupleProps & ButtonHTMLAttributes<HTMLButtonElement>
>((props, ref) => (
  <ScButtonTuple ref={ref} {...props} aria-label="search">
    <div className="tuple">
      <div className="tuple_item">
        <IconComponent
          className="button-icon"
          name="magnifierLargeIcon"
          size="40px"
        />
      </div>
      <div className="tuple_item">
        <IconComponent className="button-icon" name="closeIcon" size="40px" />
      </div>
    </div>
  </ScButtonTuple>
));

export const ButtonPromo = forwardRef<
  HTMLButtonElement,
  ScTabBarButtonProps & ButtonHTMLAttributes<HTMLButtonElement>
>((props, ref) => (
  <ScTabBarButton ref={ref} {...props} aria-label="promo">
    <IconComponent className="button-icon" name="presentIcon" size="32px" />
  </ScTabBarButton>
));

export const ButtonBurger = forwardRef<
  HTMLButtonElement,
  ScButtonTupleProps & ButtonHTMLAttributes<HTMLButtonElement>
>(({ ...props }, ref) => (
  <ScButtonTuple ref={ref} {...props} aria-label="menu">
    <div className="tuple">
      <div className="tuple_item">
        <IconComponent className="button-icon" name="menuIcon" size="40px" />
      </div>
      <div className="tuple_item">
        <IconComponent className="button-icon" name="closeIcon" size="40px" />
      </div>
    </div>
  </ScButtonTuple>
));

export const ScrollTopButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> &
    ScScrollTopButtonProps & { isTop: boolean }
>(({ isTop, ...props }, ref) => {
  const handleScrollTop = () =>
    window.scrollTo({
      behavior: 'smooth',
      top: 0,
    });
  const useCommonStore = useGetCommonStore();

  const tabBarHeight = useCommonStore.use.tabBarHeight();

  return isTop ? null : (
    <ScScrollTopButton
      bottom={tabBarHeight}
      onClick={handleScrollTop}
      ref={ref}
      {...props}
    >
      <div className="arrow" />
    </ScScrollTopButton>
  );
});

export const ButtonHome = forwardRef<
  HTMLButtonElement,
  ScTabBarButtonProps & ButtonHTMLAttributes<HTMLButtonElement>
>((props, ref) => (
  <ScTabBarButton ref={ref} {...props}>
    <IconComponent className="button-icon" name="casinoIcon" size="40px" />
  </ScTabBarButton>
));

export { Button } from './Button';
export { RadioButton } from './RadioButton';
